import React, { useState, useEffect } from 'react';
import { X, Package, Eye, Check, AlertCircle, Loader2, Download, Trash2, RefreshCw } from 'lucide-react';
import { DeliveryPersonService, DeliveryPersonItem } from '../services/deliveryPersonService';
import { useAuth } from '../utils/AuthContext';

interface SessionDeliveryPersonItem {
  id: string;
  item_sku: string;
  item_id?: string;
  quantity: number;
  unit_price: number;
  unit_cost: number;
  total_amount: number;
  order_references?: string;
  delivery_person_order_data?: any;
  created_at: string;
  updated_at: string;
  item?: {
    id: string;
    name: string;
    sku: string;
    barcode?: string;
    category?: string;
    brand?: string;
    shelves?: Array<{
      id: string;
      shelf_name: string;
    }>; // Array of shelf objects with IDs
    expiries?: Array<{
      id: string;
      expiry_date: string;
      reminder_days?: number;
      batch_number?: string;
      quantity?: number;
      notes?: string;
      is_reminded?: boolean;
    }>;
  };
  current_stock: number;
  average_cost: number;
  total_value: number;
  primary_image?: string;
}

interface DeliveryPersonItemListModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  deliveryHash?: string;
  onItemsImported?: (count: number) => void;
  onSessionCleared?: () => void; // Callback when session delivery items are cleared
  cartItems: any[]; // Current cart items to compare against
}

export const DeliveryPersonItemListModal: React.FC<DeliveryPersonItemListModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  deliveryHash,
  onItemsImported,
  onSessionCleared,
  cartItems = []
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [deliveryPersonItems, setDeliveryPersonItems] = useState<DeliveryPersonItem[]>([]);
  const [sessionDeliveryPersonItems, setSessionDeliveryPersonItems] = useState<SessionDeliveryPersonItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [viewMode, setViewMode] = useState<'delivery_person' | 'session'>('delivery_person');

  // Load delivery person items when modal opens
  useEffect(() => {
    if (isOpen && deliveryHash) {
      loadDeliveryPersonItems();
      loadSessionDeliveryPersonItems();
    }
  }, [isOpen, deliveryHash, sessionId]);

  // Refresh display when cart changes (for accurate quantity comparison)
  useEffect(() => {
    if (isOpen) {
      // Just trigger a re-render, no need to reload data
      // The cart comparison will update automatically
    }
  }, [cartItems, isOpen]);

  const loadDeliveryPersonItems = async (forceRefresh = false) => {
    if (!deliveryHash) return;

    setLoadingItems(true);
    setError(null);

    try {
      // Use refresh function if force refresh is requested
      const result = forceRefresh 
        ? await DeliveryPersonService.refreshDeliveryPersonItems(deliveryHash)
        : await DeliveryPersonService.getDeliveryPersonItems(deliveryHash);
      
      if (result.error && result.error !== 'Already loading') {
        setError(result.error);
        setDeliveryPersonItems([]);
      } else {
        setDeliveryPersonItems(result.data || []);
      }
    } catch (error: any) {
      console.error('Error loading delivery person items:', error);
      setError('Failed to load delivery person items');
      setDeliveryPersonItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const loadSessionDeliveryPersonItems = async () => {
    if (!sessionId) return;

    try {
      const result = await DeliveryPersonService.getSessionDeliveryPersonItems(sessionId, user?.id);
      
      if (result.error) {
        console.error('Error loading session delivery person items:', result.error);
        setSessionDeliveryPersonItems([]);
      } else {
        setSessionDeliveryPersonItems(result.data || []);
      }
    } catch (error: any) {
      console.error('Error loading session delivery person items:', error);
      setSessionDeliveryPersonItems([]);
    }
  };

  const handleImportList = async () => {
    if (!deliveryPersonItems.length) {
      setError('No items to import');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const result = await DeliveryPersonService.importItemsToSession(
        sessionId,
        deliveryPersonItems,
        user?.id
      );

      if (result.error) {
        setError(result.error);
      } else {
        console.log('✅ Import successful:', result.data);
        
        // Reload session items to show updated list
        await loadSessionDeliveryPersonItems();
        
        // Switch to session view to show imported items
        setViewMode('session');
        
        // Notify parent component
        if (onItemsImported) {
          onItemsImported(result.data?.imported_count || 0);
        }
      }
    } catch (error: any) {
      console.error('Error importing delivery person items:', error);
      setError('Failed to import items');
    } finally {
      setImporting(false);
    }
  };

  const handleRemoveFromSession = async (itemSku: string) => {
    try {
      const result = await DeliveryPersonService.removeItemFromSession(
        sessionId,
        itemSku,
        user?.id
      );

      if (result.error) {
        setError(result.error);
      } else {
        // Reload session items
        await loadSessionDeliveryPersonItems();
      }
    } catch (error: any) {
      console.error('Error removing item from session:', error);
      setError('Failed to remove item');
    }
  };

  const handleClearSession = async () => {
    if (!window.confirm('Are you sure you want to clear all delivery person items from this session?')) {
      return;
    }

    try {
      const result = await DeliveryPersonService.clearSessionDeliveryPersonItems(sessionId, user?.id);

      if (result.error) {
        setError(result.error);
      } else {
        // Reload session items
        await loadSessionDeliveryPersonItems();
        
        // Notify parent component that session was cleared
        if (onSessionCleared) {
          onSessionCleared();
        }
      }
    } catch (error: any) {
      console.error('Error clearing session delivery person items:', error);
      setError('Failed to clear session items');
    }
  };

  const getItemInCart = (sku: string) => {
    return cartItems.find(item => item.sku === sku);
  };

  const getCartQuantity = (sku: string) => {
    const cartItem = getItemInCart(sku);
    return cartItem?.cartQuantity || 0;
  };

  const getStockLevelColor = (stock: number) => {
    if (stock < 0) return 'text-red-600 bg-red-50';
    if (stock === 0) return 'text-orange-600 bg-orange-50';
    if (stock <= 5) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  };

  const renderDeliveryPersonItems = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Delivery Person Items ({deliveryPersonItems.length})
        </h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => loadDeliveryPersonItems(true)}
            disabled={loadingItems}
            className="px-4 py-3 sm:px-3 sm:py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center justify-center sm:justify-start gap-2 touch-manipulation min-h-[44px] sm:min-h-0"
            title="Force refresh from server (bypasses cache)"
          >
            <RefreshCw className={`w-4 h-4 ${loadingItems ? 'animate-spin' : ''}`} />
            <span className="sm:inline">Refresh</span>
          </button>
          <button
            onClick={handleImportList}
            disabled={importing || !deliveryPersonItems.length}
            className="px-4 py-3 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 touch-manipulation min-h-[44px] sm:min-h-0"
          >
            {importing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>Import List</span>
          </button>
        </div>
      </div>

      {loadingItems ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading items...</span>
        </div>
      ) : deliveryPersonItems.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No items found for this delivery person</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {deliveryPersonItems.map((item) => {
            const cartItem = getItemInCart(item.sku);
            const isInCart = !!cartItem;
            const cartQuantity = getCartQuantity(item.sku);
            
            return (
              <div
                key={item.sku}
                className={`p-3 sm:p-4 border rounded-lg ${
                  isInCart ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-start sm:items-center gap-3">
                  {/* Item Image */}
                  <div className="w-16 h-16 sm:w-12 sm:h-12 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                    {item.image_url || item.primary_image ? (
                      <img
                        src={item.image_url || item.primary_image}
                        alt={item.name || item.sku}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                      <h4 className="font-medium text-gray-900 text-sm sm:text-base leading-tight">
                        {item.name || item.sku}
                      </h4>
                      {isInCart && (
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 self-start sm:self-auto" />
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500 mb-2">SKU: {item.sku}</p>
                    
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                      <span className="text-sm font-medium bg-gray-100 px-2 py-1 rounded-full">
                        Qty: {item.totalQuantity}
                      </span>
                      
                      {item.current_stock !== undefined && (
                        <span className={`text-xs px-2 py-1 rounded-full ${getStockLevelColor(item.current_stock)}`}>
                          Stock: {item.current_stock}
                        </span>
                      )}
                      
                      {isInCart && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          In Cart: {cartQuantity}
                        </span>
                      )}
                    </div>

                    {item.items && item.items.length > 0 && (
                      <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                        Orders: {item.items.map(order => order.orderNumber).join(', ')}
                      </p>
                    )}
                  </div>

                  {/* Status Indicator */}
                  <div className="flex-shrink-0 self-start sm:self-center">
                    {isInCart ? (
                      <div className="w-10 h-10 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <Check className="w-5 h-5 sm:w-4 sm:h-4 text-green-600" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 sm:w-8 sm:h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <Package className="w-5 h-5 sm:w-4 sm:h-4 text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderSessionItems = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Session Delivery Items ({sessionDeliveryPersonItems.length})
        </h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={loadSessionDeliveryPersonItems}
            className="px-4 py-3 sm:px-3 sm:py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center sm:justify-start gap-2 touch-manipulation min-h-[44px] sm:min-h-0"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          {sessionDeliveryPersonItems.length > 0 && (
            <button
              onClick={handleClearSession}
              className="px-4 py-3 sm:px-3 sm:py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center justify-center sm:justify-start gap-2 touch-manipulation min-h-[44px] sm:min-h-0"
            >
              <Trash2 className="w-4 h-4" />
              <span>Clear All</span>
            </button>
          )}
        </div>
      </div>

      {sessionDeliveryPersonItems.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No delivery person items in this session</p>
          {deliveryPersonItems.length > 0 && (
            <p className="text-sm mt-2">Import items from the delivery person list to get started</p>
          )}
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {sessionDeliveryPersonItems.map((item) => {
            const cartItem = getItemInCart(item.item_sku);
            const isInCart = !!cartItem;
            const cartQuantity = getCartQuantity(item.item_sku);
            
            return (
              <div
                key={item.id}
                className={`p-3 sm:p-4 border rounded-lg ${
                  isInCart ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-start sm:items-center gap-3">
                  {/* Item Image */}
                  <div className="w-16 h-16 sm:w-12 sm:h-12 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                    {item.primary_image ? (
                      <img
                        src={item.primary_image}
                        alt={item.item?.name || item.item_sku}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                      <h4 className="font-medium text-gray-900 text-sm sm:text-base leading-tight">
                        {item.item?.name || item.item_sku}
                      </h4>
                      {isInCart && (
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 self-start sm:self-auto" />
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-500 mb-2">SKU: {item.item_sku}</p>
                    
                    {/* Shelf Tags */}
                    {item.item?.shelves && item.item.shelves.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {item.item.shelves.slice(0, 3).map((shelf: any, index: number) => (
                          <span 
                            key={shelf.id || index}
                            className="inline-block bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded-full font-medium"
                          >
                            {shelf.shelf_name}
                          </span>
                        ))}
                        {item.item.shelves.length > 3 && (
                          <span className="text-xs text-purple-600">
                            +{item.item.shelves.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        Session: {item.quantity}
                      </span>
                      
                      {item.current_stock !== undefined && (
                        <span className={`text-xs px-2 py-1 rounded-full ${getStockLevelColor(item.current_stock)}`}>
                          Stock: {item.current_stock}
                        </span>
                      )}
                      
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        isInCart 
                          ? cartQuantity === item.quantity 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        Cart: {cartQuantity}
                        {isInCart && cartQuantity !== item.quantity && (
                          <span className="ml-1">
                            {cartQuantity > item.quantity ? '↑' : '↓'}
                          </span>
                        )}
                      </span>
                    </div>

                    {item.order_references && (
                      <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                        Orders: {item.order_references}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row items-center gap-2 flex-shrink-0 self-start sm:self-center">
                    {isInCart ? (
                      <div className="w-10 h-10 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <Check className="w-5 h-5 sm:w-4 sm:h-4 text-green-600" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 sm:w-8 sm:h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <Package className="w-5 h-5 sm:w-4 sm:h-4 text-gray-400" />
                      </div>
                    )}
                    
                    <button
                      onClick={() => handleRemoveFromSession(item.item_sku)}
                      className="w-10 h-10 sm:w-8 sm:h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200 touch-manipulation"
                      title="Remove from session"
                    >
                      <X className="w-5 h-5 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-sm sm:max-w-lg md:max-w-2xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Delivery Person Item List
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Manage delivery person items for this session
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 sm:p-1 touch-manipulation min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex mt-4 space-x-1 overflow-x-auto">
            <button
              onClick={() => setViewMode('delivery_person')}
              className={`px-3 py-2 sm:px-4 sm:py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                viewMode === 'delivery_person'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              Delivery Person List
            </button>
            <button
              onClick={() => setViewMode('session')}
              className={`px-3 py-2 sm:px-4 sm:py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                viewMode === 'session'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              Session Items
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-180px)] sm:max-h-[calc(90vh-200px)]">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {viewMode === 'delivery_person' ? renderDeliveryPersonItems() : renderSessionItems()}
        </div>
      </div>
    </div>
  );
};
